import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { Route, Routes, useLocation } from "react-router-dom";
import {
  AllInboxOutlined,
  AllInboxRounded,
  CategoryOutlined,
  CategoryRounded,
  GamepadOutlined,
  GamepadRounded,
  LogoutRounded,
  MenuRounded,
  QuestionMarkOutlined,
  QuestionMarkRounded,
} from "@mui/icons-material";
import {
  COLOR_BLACK,
  COLOR_SLIGHTLY_DARKER_WHITE,
  COLOR_WHITE,
  GRADIENT_GRAY,
  GRADIENT_MAIN,
} from "../../../data/colors/Colors";
import { Stack } from "@mui/material";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { DrawerItem, DrawerItemView } from "./DrawerItemView";
import {
  STRINGS_ACTIONS,
  STRINGS_ADMIN_PANEL,
  STRINGS_CATEGORIES,
  STRINGS_LOGOUT,
} from "../../../data/strings/Strings";
import { Error404View } from "../../component/404View";
import { CategoriesView } from "../games/categories/CategoriesView";
import { QuestionsView } from "../games/trivia/questions/QuestionsView";
import { CategoryDetailsView } from "../games/categories/categoryDetails/CategoryDetailsView";
import { QuestionDetailsView } from "../games/trivia/questions/questionDetails/QuestionDetailsView";
import { CustomTextView } from "../../component/CustomTextView";
import { HomeViewModel } from "./HomeViewModel";
import { provideGetAdminInfoUseCase } from "../../di/UseCasesModule";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { StatsView } from "../stats/StatsView";

const drawerWidth = 320;

export function HomeView() {
  HomeViewModel(provideGetAdminInfoUseCase());
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const location = useLocation();
  const { width } = useWindowDimensions();

  const statsGamesTriviaDrawerRouterItems: Array<DrawerItem> = [
    {
      isRoute: true,
      title: "All",
      icon: <AllInboxOutlined />,
      selectedIcon: <AllInboxRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_STATS,
      href: ROUTE_ENDPOINTS.HOME_STATS,
      onCloseDrawer: () => setMobileOpen(false),
    },
  ];

  const categoryDrawerRouterItems: Array<DrawerItem> = [
    {
      isRoute: true,
      title: STRINGS_CATEGORIES,
      icon: <CategoryOutlined />,
      selectedIcon: <CategoryRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_CATEGORIES,
      href: ROUTE_ENDPOINTS.HOME_CATEGORIES,
      onCloseDrawer: () => setMobileOpen(false),
    },
  ];

  const triviaDrawerRouterItems: Array<DrawerItem> = [
    {
      isRoute: true,
      title: "Questions",
      icon: <QuestionMarkOutlined />,
      selectedIcon: <QuestionMarkRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_QUESTIONS,
      href: ROUTE_ENDPOINTS.HOME_QUESTIONS,
      onCloseDrawer: () => setMobileOpen(false),
    },
  ];

  const drawerButtonsItems: Array<DrawerItem> = [
    {
      isRoute: false,
      title: STRINGS_LOGOUT,
      icon: <LogoutRounded />,
      onClick: () => {
        SharedPreferences.clear();
      },
    },
  ];

  const drawer = (
    <Stack
      sx={{
        background: COLOR_SLIGHTLY_DARKER_WHITE,
        height: "100%",
        overflow: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Toolbar />
      <Stack spacing={0} paddingBottom={2}>
        <CustomTextView
          text={"Statistics"}
          props={{
            paddingTop: 2,
            paddingLeft: 2,
            paddingRight: 2,
          }}
          fontSize={16}
          fontWeight="300"
          textColor={COLOR_BLACK}
        />
        {statsGamesTriviaDrawerRouterItems.map((item) => (
          <DrawerItemView
            icon={item.icon}
            selectedIcon={item.selectedIcon}
            key={item.title}
            isRoute={item.isRoute}
            title={item.title}
            isSelected={item.isSelected}
            href={item.href}
            onCloseDrawer={item.onCloseDrawer}
          />
        ))}
        <Divider sx={{ marginTop: 2 }} />
        <CustomTextView
          text={"Trivia"}
          props={{
            paddingTop: 2,
            paddingLeft: 2,
            paddingRight: 2,
          }}
          fontSize={16}
          fontWeight="300"
          textColor={COLOR_BLACK}
        />

        {categoryDrawerRouterItems.map((item) => (
          <DrawerItemView
            icon={item.icon}
            selectedIcon={item.selectedIcon}
            key={item.title}
            isRoute={item.isRoute}
            title={item.title}
            isSelected={item.isSelected}
            href={item.href}
            onCloseDrawer={item.onCloseDrawer}
          />
        ))}
        {triviaDrawerRouterItems.map((item) => (
          <DrawerItemView
            icon={item.icon}
            selectedIcon={item.selectedIcon}
            key={item.title}
            isRoute={item.isRoute}
            title={item.title}
            isSelected={item.isSelected}
            href={item.href}
            onCloseDrawer={item.onCloseDrawer}
          />
        ))}
      </Stack>
      <Divider sx={{ marginTop: 2 }} />
      <CustomTextView
        text={STRINGS_ACTIONS}
        props={{
          paddingTop: 2,
          paddingLeft: 2,
          paddingRight: 2,
        }}
        fontSize={16}
        fontWeight="300"
        textColor={COLOR_BLACK}
      />
      {drawerButtonsItems.map((item) => (
        <DrawerItemView
          key={item.title}
          icon={item.icon}
          isRoute={item.isRoute}
          onClick={item.onClick}
          title={item.title}
        />
      ))}
    </Stack>
  );
  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: GRADIENT_MAIN,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: 0, mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <CustomTextView
            text={STRINGS_ADMIN_PANEL}
            variant="h5"
            fontWeight="bold"
            textColor={COLOR_WHITE}
          />
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Stack
        sx={{
          flexGrow: 1,
          background: GRADIENT_GRAY,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        <Box
          component="main"
          sx={{
            m: width < 900 ? 2 : 3,
            border: "1px solid lightGray",
            borderRadius: 8,
            background: COLOR_WHITE,
            minHeight: "100vh",
          }}
        >
          <Routes>
            <Route
              path={ROUTE_ENDPOINTS.CATEGORIES}
              element={<CategoriesView />}
            />
            <Route
              path={`${ROUTE_ENDPOINTS.CATEGORIES}/:id`}
              element={<CategoryDetailsView />}
            />
            <Route
              path={ROUTE_ENDPOINTS.QUESTIONS}
              element={<QuestionsView />}
            />
            <Route
              path={`${ROUTE_ENDPOINTS.QUESTIONS}/:id`}
              element={<QuestionDetailsView />}
            />
            <Route
              path={ROUTE_ENDPOINTS.STATS}
              element={<StatsView />}
            />

            <Route path="*" element={<Error404View />} />
          </Routes>
        </Box>
      </Stack>
    </Box>
  );
}
